import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const FeedbackForm = ({ auditId, callId, initialFeedback, initialRecordedCorrectlyRating, initialPronouncedIntroRating, onSave }) => {
  const [feedback, setFeedback] = useState(initialFeedback || '');
  const [recordedCorrectlyRating, setRecordedCorrectlyRating] = useState(initialRecordedCorrectlyRating || null);
  const [pronouncedIntroRating, setPronouncedIntroRating] = useState(initialPronouncedIntroRating || null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    setFeedback(initialFeedback || '');
    setRecordedCorrectlyRating(initialRecordedCorrectlyRating || null);
    setPronouncedIntroRating(initialPronouncedIntroRating || null);
    setIsDirty(false);
  }, [initialFeedback, initialRecordedCorrectlyRating, initialPronouncedIntroRating]);

  const handleFeedbackChange = (e) => {
    setFeedback(e.target.value);
    setIsDirty(true);
  };

  const handleRecordedCorrectlyRatingChange = (value) => {
    setRecordedCorrectlyRating(value);
    setIsDirty(true);
  };

  const handlePronouncedIntroRatingChange = (value) => {
    setPronouncedIntroRating(value);
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!isDirty) return;

    setIsSaving(true);
    setError(null);

    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated.');

      // Check if feedback already exists (checkError: PGRST116 = no rows, which is expected)
      const { data: existingFeedback, error: checkError } = await supabase
        .from('audit_call_feedback')
        .select('id')
        .eq('audit_id', auditId)
        .eq('call_id', callId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') throw checkError;

      let result;
      if (existingFeedback) {
        // Update existing feedback
        const { data, error: updateError } = await supabase
          .from('audit_call_feedback')
          .update({
            feedback: feedback || null,
            recorded_correctly_rating: recordedCorrectlyRating || null,
            pronounced_intro_rating: pronouncedIntroRating || null,
            reviewed_at: new Date().toISOString(),
            reviewed_by: user.id
          })
          .eq('id', existingFeedback.id)
          .select()
          .single();

        if (updateError) throw updateError;
        result = data;
      } else {
        // Create new feedback
        const { data, error: insertError } = await supabase
          .from('audit_call_feedback')
          .insert({
            audit_id: auditId,
            call_id: callId,
            feedback: feedback || null,
            recorded_correctly_rating: recordedCorrectlyRating || null,
            pronounced_intro_rating: pronouncedIntroRating || null,
            reviewed_at: new Date().toISOString(),
            reviewed_by: user.id
          })
          .select()
          .single();

        if (insertError) throw insertError;
        result = data;
      }

      setIsDirty(false);
      if (onSave) {
        onSave(result);
      }
    } catch (e) {
      console.error('Failed to save feedback:', e);
      setError(e.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-xs">
          {error}
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Recorded Correctly Rating (Optional)
        </label>
        <div className="flex space-x-1">
          {[1, 2, 3, 4, 5].map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => handleRecordedCorrectlyRatingChange(value)}
              className={`w-8 h-8 rounded text-sm font-medium transition-colors ${
                recordedCorrectlyRating === value
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              {value}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Pronounced Intro Correctly Rating (Optional)
        </label>
        <div className="flex space-x-1">
          {[1, 2, 3, 4, 5].map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => handlePronouncedIntroRatingChange(value)}
              className={`w-8 h-8 rounded text-sm font-medium transition-colors ${
                pronouncedIntroRating === value
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              {value}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Feedback
        </label>
        <textarea
          value={feedback}
          onChange={handleFeedbackChange}
          rows={3}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-boxdark-2 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 resize-none"
          placeholder="Enter your feedback here..."
        />
      </div>

      {isDirty && (
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="w-full px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : 'Save Feedback'}
        </button>
      )}

      {!isDirty && (feedback || recordedCorrectlyRating || pronouncedIntroRating) && (
        <div className="text-xs text-green-600 dark:text-green-400">
          ✓ Feedback saved
        </div>
      )}
    </div>
  );
};

export default FeedbackForm;

